import React, { forwardRef } from 'react';
import { DateField, LoadingView } from '@cuatroochenta/co-admin-react-library';
import './PopUpNews.scss';
import { withTranslation, WithTranslation } from 'react-i18next';
import { translate } from '../../../../../I18n/i18n';
import { Post, PostDetail } from '../../../../model/domain/news';
import { User } from '../../../../model/domain/user';
import PopUpRemove from '../../../common/components/PopUpRemove/PopUpRemove';
import { MediaFile } from '../../../common/model/media';
import { ShareOptionsUtils } from '../../../../../utils/map-utils';
import TaskNews from '../../../../ws/TaskNews';
import NewsCardProfilePicture from '../NewsCardProfilePicture/NewsCardProfilePicture';
import { ShareCenterOption } from '../../../../model/domain/share-option';
import AdvancedShareWithCentersSelector from '../../../common/components/AdvancedShareWithCentersSelector/AdvancedShareWithCentersSelector';
import AdvancedShareWithCentersToolbar from '../../../common/components/AdvancedShareWithCentersToolbar/AdvancedShareWithCentersToolbar';

interface PopUpNewsProps extends WithTranslation {
    show: boolean;
    onClose: (post: Post | undefined) => void;
    onSubmit: (data: { content: string; media: File[] | File; isForAllCenters: string, publishingDate: string | null, isCreate: boolean, mediaType: string | null, shareOptions: ShareCenterOption[] }) => Promise<void>;
    post: PostDetail | undefined;
    taskNewsService: TaskNews;
    user: User;
    forwardedRef?: React.Ref<any>;
}

interface PopUpNewsState {
    content: string;
    media: string[] | File;
    isForAllCenters: boolean;
    publishingDate: string;
    post: PostDetail | undefined;
    isLoadingMedia: boolean;
    isVideoPreviewError: boolean;
    isSubmitting: boolean;
    showPopupRemove: boolean;
    mediaIdToRemove: number;
    newMedia: File[] | File;
    isNewMedia: boolean;
    combinedMedia: MediaFile[];
    showPublishAll: boolean;
    errorMessage: string;
    loadingShareOptions: boolean;
    shareOptions?: ShareCenterOption[]
    selectedNames: string[]
}

class PopUpNews extends React.Component<PopUpNewsProps, PopUpNewsState> {
    imageFileInputRef = React.createRef<HTMLInputElement>();
    videoFileInputRef = React.createRef<HTMLInputElement>();

    constructor(props: PopUpNewsProps) {
        super(props);
        this.imageFileInputRef = React.createRef();
        this.videoFileInputRef = React.createRef();
        this.state = {
            content: props.post?.content || '',
            media: [],
            isForAllCenters: false,
            publishingDate: '',
            post: props.post,
            isLoadingMedia: false,
            isVideoPreviewError: false,
            isSubmitting: false,
            showPopupRemove: false,
            mediaIdToRemove: -1,
            newMedia: [],
            isNewMedia: false,
            combinedMedia: [],
            showPublishAll: false,
            errorMessage: '',
            loadingShareOptions: false,
            selectedNames: [],
            shareOptions: this.props.post?.shareOptions
        };
    }

    componentDidMount() {
        // Añadir o eliminar la clase para desactivar el scroll del body
        if (this.props.show) {
            document.body.classList.add('no-scroll');
        }
    }

    componentWillUnmount() {
        document.body.classList.remove('no-scroll');
    }

    componentDidUpdate(prevProps: PopUpNewsProps, prevState: PopUpNewsState) {
        if (this.props.show !== prevProps.show) {
            if (this.props.show) {
                document.body.classList.add('no-scroll');
                if (!this.props.post) {
                    this.loadShareOptions();
                }
            } else {
                document.body.classList.remove('no-scroll');
            }
        }

        if (this.props.post !== prevProps.post) {
            this.setState({
                content: this.props.post?.content || '',
                post: this.props.post,
                isForAllCenters: false,
                publishingDate: this.props.post?.publishingDate || '',
                media: [],
                isLoadingMedia: !!this.props.post?.media,
                shareOptions: this.props.post?.shareOptions
            });

            if (this.props.post?.media) {
                // Si hay medios en el post, cargamos los archivos
                this.loadMedia(this.props.post.media);
            }
        }

        if (prevState.media !== this.state.media || prevState.newMedia !== this.state.newMedia) {
            const combinedMedia: MediaFile[] = [
                ...(Array.isArray(this.state.media) ? this.state.media.map(url => ({ url, isNewMedia: false })) : []),
                ...(Array.isArray(this.state.newMedia) ? this.state.newMedia.map(file => ({ file, isNewMedia: true })) : [])
            ];

            this.setState({ combinedMedia });
        }

        if (prevState.shareOptions !== this.state.shareOptions) {
            const selectedNames = ShareOptionsUtils.getSelectedNames(this.state.shareOptions)
            this.setState({ selectedNames })
        }
    }

    loadMedia = async (media: { media_type: string; data: Array<{ id: number; size?: number; data: string; }> }) => {
        const mediaUrls: string[] = [];

        if (media.data) {
            // Iteramos sobre los medios y cargamos cada archivo
            for (const item of media.data) {
                try {
                    let url: string | null = null;

                    if (media.media_type === 'video') {
                        // Si es video, llamamos a getVideo
                        url = this.props.taskNewsService.getVideo(item.id);
                    } else if (media.media_type === 'images') {
                        // Si es imagen, llamamos a fetchMediaById
                        url = this.props.taskNewsService.getMediaUrlById(item.id.toString(), media.media_type);
                    }

                    if (url) {
                        mediaUrls.push(url);
                    }
                } catch (error) {
                    console.error('Error al cargar el medio', error);
                }
            }

            // Actualizamos el estado con los medios cargados
            this.setState({ media: mediaUrls, isLoadingMedia: false });
        } else {
            this.setState({ media: [], isLoadingMedia: false });
        }


    };

    private loadShareOptions = async () => {
        try {
            this.setState({ loadingShareOptions: true });
            const shareOptions = await this.props.taskNewsService.getShareOptions();
            this.setState({
                shareOptions,
                loadingShareOptions: false
            });
        } catch (error) {
            console.error(`Error getting news share options: ${error}`);
        }
    };

    handleSubmit = async () => {
        const { content, isForAllCenters, publishingDate, post, newMedia, shareOptions } = this.state;
        const isCreate = !post;

        if (content) {
            this.setState({ isSubmitting: true });

            let mediaType: string | null = null; // Para indicar si es "images" o "video"
            let newMediaToSend: File[] = [];

            const mediaArray = Array.isArray(newMedia) ? newMedia : [newMedia];

            if (mediaArray.length > 0) {
                if (mediaArray[0].type.startsWith("image")) {
                    mediaType = 'images'; // Si hay más de un archivo, se asume que son imágenes
                    newMediaToSend = mediaArray;
                } else if (mediaArray[0].type.startsWith("video")) {
                    mediaType = 'video';
                    newMediaToSend = mediaArray;
                }
            }

            const isForAllCentersString = (isForAllCenters === true) ? 'true' : 'false';

            try {
                await this.props.onSubmit({
                    content,
                    media: newMediaToSend,
                    isForAllCenters: isForAllCentersString,
                    publishingDate: publishingDate ?? null,
                    isCreate,
                    mediaType,
                    shareOptions: shareOptions ?? []
                });
            } finally {
                this.setState({ isSubmitting: false, newMedia: [] });
            }
        } else {
            alert(translate(this.props.t, "news.TR_NEWS_CONTENT_REQUIRED"));
        }
    };

    handleImagePicker = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files);

            const imageTypes = ['image/jpeg', 'image/png'];

            const images = fileArray.filter(file => imageTypes.includes(file.type));

            this.setState(prevState => ({
                newMedia: Array.isArray(prevState.newMedia)
                    ? [...prevState.newMedia, ...images]
                    : [...images]
            }));

            if (this.imageFileInputRef.current) {
                this.imageFileInputRef.current.value = "";
            }
        }
    };

    handleVideoPicker = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        let errorMessage = '';
    
        if (files) {
            this.setState({isSubmitting: true});
            const fileArray = Array.from(files);
            const validVideos = fileArray.filter((file) => {
    
                if (!file.type.startsWith('video')) {
                    errorMessage = translate(this.props.t, 'news.TR_INVALID_VIDEO_FILE', { fileName: file.name });
                    return false;
                }
    
                return true;
            });
    
            const videoToProcess = validVideos.length > 0 ? validVideos[0] : null;
    
            if (videoToProcess) {
                this.setState({
                    newMedia: [videoToProcess],
                    errorMessage: '',
                    isSubmitting: false
                });
            }
    
            if (validVideos.length === 0) {
                e.target.value = ''; 
            }

            if (this.videoFileInputRef.current) {
                this.videoFileInputRef.current.value = "";
            }
        }
    };

    handleRemoveMedia = () => {
        const { post, media, mediaIdToRemove, newMedia, isNewMedia } = this.state;

        if (!Array.isArray(media)) {
            console.error('El tipo de "media" no es un arreglo.');
            return;
        }

        if (isNewMedia) {
            if (Array.isArray(newMedia) && mediaIdToRemove < newMedia.length) {
                const updatedMedia = [...newMedia];
                updatedMedia.splice(mediaIdToRemove, 1);
                this.setState({ newMedia: updatedMedia, showPopupRemove: false });
                return;
            }
        }

        if (mediaIdToRemove < 0 || mediaIdToRemove >= media.length) {
            console.error('Índice fuera de rango.');
            return;
        }

        const updatedMedia = [...media];
        updatedMedia.splice(mediaIdToRemove, 1);

        if (post) {
            if (!post || !post.media || !Array.isArray(post.media.data)) {
                this.setState({ media: updatedMedia, showPopupRemove: false });
                return;
            }

            if (!post.id) {
                console.error('El post no tiene un ID válido.');
                return;
            }

            if (post?.media?.data?.[mediaIdToRemove]) {
                this.props.taskNewsService.removeMedia(
                    post.media?.data[mediaIdToRemove].id,
                    post.media?.media_type,
                    post.id
                )
                    .then(() => {
                        const updatedMedia = [...media];
                        updatedMedia.splice(mediaIdToRemove, 1);
                        // Actualizar el post en el estado para reflejar el cambio en los medios
                        const updatedPost = {
                            ...post,
                            media: {
                                ...post.media,
                                data: post.media?.data ? post.media.data.filter((_, index) => index !== mediaIdToRemove) : [],
                                media_type: post.media?.media_type ?? ''
                            },
                        };

                        this.setState({ media: updatedMedia, post: updatedPost, showPopupRemove: false });
                    })
                    .catch((error: any) => {
                        console.error('Error al eliminar el medio:', error);
                    });
            } else {
                const updatedMedia = [...media];
                updatedMedia.splice(mediaIdToRemove, 1);
                this.setState({ media: updatedMedia });
            }

        } else {
            const updatedMedia = [...media];
            updatedMedia.splice(mediaIdToRemove, 1);
            this.setState({ media: updatedMedia });
        }
    };

    handleAddImageClick = () => {
        if (this.imageFileInputRef.current) {
            this.imageFileInputRef.current.click();
        }
    };

    togglePopupRemove = (index: number, isNewMedia: boolean) => {
        this.setState({ showPopupRemove: !this.state.showPopupRemove, mediaIdToRemove: index, isNewMedia: isNewMedia, isVideoPreviewError: false });
    };


    renderMediaPreview = () => {
        const { t } = this.props; 
        const { isLoadingMedia, combinedMedia, isVideoPreviewError } = this.state;

        if (!combinedMedia || combinedMedia.length === 0) {
            return null;
        }

        if (isLoadingMedia) {
            return <LoadingView loading={true} />;
        }

        // Comprobamos si hay medios para renderizar
        return (
            <div className="media-container">
                {/* Renderizamos los medios de 'media' */}
                {combinedMedia.length > 0 && (
                    <div className="media-section">
                        <div className={`media-preview-container ${combinedMedia.length === 1 ? 'one-image' : combinedMedia.length === 2 ? 'two-images' : combinedMedia.length === 3 ? 'three-images' : ''}`}>
                            {combinedMedia.map((mediaFile, index) => {
                                if (!mediaFile) return null;
                                const isVideo = mediaFile.file?.type.startsWith('video') || mediaFile.url?.includes('video');
                                const mediaLength = Array.isArray(this.state.media) ? this.state.media.length : 0;
                                let originalIndex = index;
                                if (index >= mediaLength) {
                                    originalIndex = index - mediaLength;
                                }
                                const mediaSource = mediaFile.file
                                    ? URL.createObjectURL(mediaFile.file)
                                    : mediaFile.url

                                return (
                                    <div key={index} className="media-preview">
                                        <button
                                            className="remove-button"
                                            onClick={() => this.togglePopupRemove(originalIndex, mediaFile.isNewMedia)}
                                        >
                                            &times;
                                        </button>

                                        {isVideo ? (
                                            <video controls className="media-file" onError={this.handleVideoPreviewError}>
                                                <source src={mediaSource!} />
                                                {translate(t, 'videos.TR_VIDEO_NOT_SUPPORTED')}
                                            </video>
                                        ) : (
                                            <img
                                                src={mediaSource}
                                                style={{background: "center / cover url('/icons/media_fallback.png')"}}
                                                alt={`preview-${index}`}
                                                className="media-file" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {isVideoPreviewError && <p>{translate(t, 'videos.TR_PREVIEW_FAILED')}</p>}
                    </div>
                )}
            </div>
        );
    };

    handleVideoPreviewError = () => {
        this.setState({ isVideoPreviewError: true });
    }

    resetState = () => {
        this.setState({
            content: '',
            media: [],
            isForAllCenters: false,
            post: undefined,
            isLoadingMedia: false,
            isVideoPreviewError: false,
            isSubmitting: false,
            showPopupRemove: false,
            mediaIdToRemove: -1,
            newMedia: [],
            isNewMedia: false,
            selectedNames: [],
            showPublishAll: false,
        });
    };

    handleAccept = () => {
        this.setState({ showPublishAll: false })
    };

    handleCancel = () => {
        this.setState({
            showPublishAll: false,
            selectedNames: [],
            shareOptions: this.state.shareOptions?.map(
                center => ShareOptionsUtils.copyCenterRecursive(center, { checked: false })
            )
        })
    };

    handleCenterSelection = (selection?: ShareCenterOption[]) => {
        this.setState({ shareOptions: selection })
    }

    handlePublishingDateChange = (value: string) => {
        this.setState({ publishingDate: value });
    }

    renderPublishAll() {
        const { shareOptions, loadingShareOptions } = this.state;

        return (
            <AdvancedShareWithCentersSelector
                loading={loadingShareOptions}
                shareOptions={shareOptions}
                onAccept={this.handleAccept}
                onCancel={this.handleCancel}
                onSelection={this.handleCenterSelection}
            />
        );
    }

    renderPublishNews() {
        const { user, t, post } = this.props;
        const { newMedia, content, publishingDate, isSubmitting, isLoadingMedia, selectedNames, loadingShareOptions } = this.state;
        const newMediaArray = Array.isArray(newMedia) ? newMedia : [];
        const hasImages = (post?.media?.media_type === 'images' && post.media.data.length > 0) || newMediaArray.some(item => item.type.startsWith('image'));
        const hasVideo = (post?.media?.media_type === 'video' && post.media.data.length > 0) || newMediaArray.some(item => item.type.startsWith('video'));
        const now = new Date();

        return (
            <div>
                <div className='popupNews-row-1'>
                    <div className='card-image-profile-popup' style={{ backgroundColor: user.data.profileColor }}>
                        <NewsCardProfilePicture
                            fullName={user.data.fullName}
                            profilePicture={user.data.profilePicturePath}
                        />
                    </div>
                    <textarea
                        id="content"
                        className='popupNews-text-content'
                        name="content"
                        value={content}
                        onChange={(e) => {
                            this.setState({ content: e.target.value });
                        }}
                        rows={5}
                    />
                </div>
                {!isSubmitting ? (
                    <div className="popupNews-publishingDate-row">
                        <label htmlFor="publishingDate">
                            {translate(
                                t,
                                'news.TR_SAVE_MODAL_PUBLISHING_DATE_LABEL'
                            )}
                        </label>
                        <DateField
                            name="publishingDate"
                            type="DATETIME"
                            value={publishingDate}
                            onChange={this.handlePublishingDateChange}
                            placeholder={translate(
                                t,
                                'news.TR_SAVE_MODAL_PUBLISHING_DATE_IMMEDIATELY'
                            )}
                            minDate={now}
                            minTime={now}
                        />
                    </div>
                ) : null}

                {isSubmitting || isLoadingMedia ? (
                    <LoadingView loading={true} />
                ) : (
                    <></>
                )}

                <div className="popupNews-data">
                    <div className='popupNews-media-button-container' style={{ display: hasImages || hasVideo ? 'none' : 'flex' }}>
                        <div className="file-upload">
                            <input
                                type="file"
                                id="image-media"
                                name="media"
                                accept="image/jpeg, image/png"
                                multiple
                                onChange={this.handleImagePicker}
                                className="file-input"
                                ref={this.imageFileInputRef}
                            />
                            <label htmlFor="image-media" className="file-label" style={{ backgroundImage: 'url(/icons/images-icon.svg)' }}></label>
                        </div>
                        <div className="file-upload">
                            <input
                                type="file"
                                id="video-media"
                                name="media"
                                accept="video/*"
                                onChange={this.handleVideoPicker}
                                className="file-input"
                                ref={this.videoFileInputRef}
                            />
                            <label htmlFor="video-media" className="file-label" style={{ backgroundImage: 'url(/icons/videos-icon.svg)' }}></label>
                        </div>
                    </div>
                    {this.renderMessageCard()}
                    {this.renderMediaPreview()}

                    {hasImages ? (
                        <button className="btn add-images" onClick={this.handleAddImageClick}>
                            {translate(t, 'news.TR_ADD_IMAGE_LABEL')}
                        </button>
                    ) : null}

                </div>
                <div className="divider"></div>
                <div className="popupNews-submit" style={{ justifyContent: 'space-between' }}>
                    <AdvancedShareWithCentersToolbar
                        selectedOptions={selectedNames.join(', ')}
                        onClick={this.handleShareWith} />

                    <button onClick={this.handleSubmit} className="btn btn-primary">
                        {post ? translate(t, 'news.TR_UPDATE_LABEL') : translate(t, 'news.TR_CREATE_LABEL')}
                    </button>
                </div>
            </div>
        );
    }

    handleShareWith = () => {
        this.setState({ showPublishAll: true });
    }

    handleClose = () => {
        const { onClose } = this.props;
        this.resetState();
        onClose(undefined);
    };

    renderMessageCard = () => {
        if (this.state.errorMessage) {
            return this.renderMessage(this.state.errorMessage, { color: 'red' });
        }
        return null;
    };
    
    renderMessage = (message: string, style: {} = {}) => {
        return (
            message ? 
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="message">
                                <label style={style}>{message}</label>
                            </div>
                        </div>
                    </div>
                </div>
            : null
        );
    };

    render() {
        const { show } = this.props;
        const { showPublishAll } = this.state;

        if (!show) return null;

        return (
            <div className="popupNews">
                <div className="popupNews-overlay" onClick={this.handleClose}></div>

                <div className="popupNews-container">
                    <button className="close-btn" onClick={this.handleClose}>
                        <i className="fas fa-times"></i>
                    </button>
                    <div className="popupNews-content">
                        {showPublishAll ? (
                            this.renderPublishAll()
                        ) : (
                            this.renderPublishNews()
                        )}

                    </div>
                    <PopUpRemove
                        show={this.state.showPopupRemove}
                        onClose={() => this.togglePopupRemove(-1, false)}
                        onSubmit={this.handleRemoveMedia}
                    />
                </div>
            </div>

        );
    }
}

export default withTranslation()(forwardRef((props: PopUpNewsProps, ref) => <PopUpNews {...props} forwardedRef={ref} />));
